import { useEffect, useState } from "react";
import { api, apiError, publicFileUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import ImageDropzone from "@/components/ImageDropzone";
import LeaveDays from "@/components/LeaveDays";
import ScheduleFields from "@/components/ScheduleFields";
import { useImageUpload } from "@/hooks/useImageUpload";
import { toast } from "sonner";
import { Loader2, Save } from "lucide-react";

const FIELDS = [
  { key: "location", label: "Clinic Location", placeholder: "Clinic name, area, city", testid: "profile-location-input", wide: true },
];

export default function DoctorProfile({ onSaved }) {
  const [doc, setDoc] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const upload = useImageUpload({ isPublic: true, onUploaded: (data) => set("avatar", publicFileUrl(data.path)) });

  useEffect(() => {
    api.get("/me/doctor-profile").then((r) => {
      setDoc(r.data);
      setForm({ avatar: r.data.avatar || "", about: r.data.about || "", location: r.data.location || "",
        work_days: r.data.work_days ?? [0, 1, 2, 3, 4, 5], start_time: r.data.start_time || "10:00", end_time: r.data.end_time || "17:00", slot_minutes: r.data.slot_minutes ?? 30 });
    }).catch((e) => toast.error(apiError(e)));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch("/me/doctor-profile", { ...form, slot_minutes: parseInt(form.slot_minutes || 30, 10) });
      setDoc(data);
      toast.success("Profile updated — visible on the public site");
      onSaved?.();
    } catch (e) { toast.error(apiError(e, "Could not save profile")); }
    finally { setSaving(false); }
  };

  if (!form) return <div className="text-slate-400 text-sm">Loading profile…</div>;

  return (
    <div className="grid lg:grid-cols-[320px_1fr] gap-6 items-start" data-testid="doctor-profile">
      <div className="bg-white rounded-2xl border border-slate-200 p-6 text-center space-y-4">
        <ImageDropzone upload={upload} testid="profile-photo-upload" label="Upload profile photo" compact onClear={() => set("avatar", "")}
          preview={form.avatar ? <img src={form.avatar} alt={doc.name} className="h-36 w-36 object-cover rounded-2xl border mx-auto" data-testid="profile-avatar-preview" /> : null} />
        <div>
          <div className="font-head text-xl font-bold text-slate-900">{doc.name}</div>
          <div className="text-teal-700 text-sm">{doc.specialty}</div>
          <div className="text-xs text-slate-400 mt-1">Name, specialty & fee are managed by the admin</div>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 p-6 space-y-5">
        <div className="grid sm:grid-cols-2 gap-4">
          {FIELDS.map((f) => (
            <div key={f.key} className={`space-y-2 ${f.wide ? "sm:col-span-2" : ""}`}>
              <Label>{f.label}</Label>
              <Input data-testid={f.testid} value={form[f.key]} onChange={(e) => set(f.key, e.target.value)} placeholder={f.placeholder} />
            </div>
          ))}
          <ScheduleFields form={form} set={set} />
          <div className="space-y-2 sm:col-span-2">
            <Label>About / Bio</Label>
            <Textarea data-testid="profile-about-input" rows={5} value={form.about} onChange={(e) => set("about", e.target.value)} placeholder="Tell patients about your expertise and approach…" />
          </div>
        </div>
        <Button data-testid="profile-save-button" onClick={save} disabled={saving} className="bg-teal-600 hover:bg-teal-700 text-white">
          {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />} Save Profile
        </Button>
      </div>
      <div className="lg:col-span-2"><LeaveDays /></div>
    </div>
  );
}
